/**
 * Sell-side IPC (Interim Payment Application) print model.
 * REUSE: buildSimplePrintDocument + letter-letterhead (same engine as SPC certificate).
 * Client-submittable commercial valuation — VAT belongs on Progress SI, not here.
 */
import React from 'react';
import type { PrintColumn, SimplePrintLine } from '@/print-engine';
import { buildSimplePrintDocument } from '@/print-engine/adapters/simple-document';
import { LINE_HEIGHT_PX } from '@/print-engine/page-template';
import { getCompanyLetterhead } from '@/print-engine/company-letterhead';
import { formatPrintDate } from '@/utils/purchaseOrderPrint';

function money(v: unknown): string {
  const n = Number(v ?? 0);
  return (Number.isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function str(v: unknown, fallback = ''): string {
  if (v == null || v === '') return fallback;
  return String(v);
}

function pct(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return str(v);
  return `${n}%`;
}

function parseJsonObject(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return {};
}

export function buildIpcPaymentApplicationPrintModel(input: {
  application: Record<string, unknown>;
  contract?: Record<string, unknown>;
  customerName?: string;
  projectLabel?: string;
}) {
  const pa = input.application;
  const c = input.contract || {};
  const currency = str(pa.currency || c.currency || c.currencyCode, 'AED');
  const status = str(pa.status, 'Draft');
  const isOfficial =
    status === 'Approved' || status === 'Certified' || status === 'Submitted';
  const appNo = str(pa.application_number || pa.applicationNumber, 'IPC');
  const contractNo = str(
    c.contractNumber || c.contract_number || pa.contract_id || pa.contractId,
    ''
  );
  const contractTitle = str(c.title || c.contract_title || c.contractTitle, '');
  const customerName =
    input.customerName ||
    str(c.customerName || c.customer_name) ||
    str(pa.customer_name || pa.customerName);
  const projectLabel =
    input.projectLabel ||
    str(c.projectLabel || c.project_label) ||
    str(
      c.project_number
        ? `${c.project_number}${c.project_name ? ` — ${c.project_name}` : ''}`
        : c.project_name
    );
  const periodFrom = formatPrintDate(str(pa.period_from || pa.periodFrom) || null);
  const periodTo = formatPrintDate(str(pa.period_to || pa.periodTo) || null);
  const periodLabel =
    periodFrom || periodTo ? `${periodFrom || '…'} → ${periodTo || '…'}` : '';

  const terms = parseJsonObject(
    pa.commercial_terms_json ?? pa.commercialTerms ?? pa.commercial_terms
  );

  const lines = Array.isArray(pa.lines) ? (pa.lines as Record<string, unknown>[]) : [];
  const sorted = [...lines].sort(
    (x, y) =>
      Number(x.line_number ?? x.lineNumber ?? 0) - Number(y.line_number ?? y.lineNumber ?? 0)
  );

  const columns: PrintColumn[] = [
    { key: 'sno', label: '#' },
    { key: 'code', label: 'Code' },
    { key: 'description', label: 'Description' },
    { key: 'entitlement', label: 'Line value', align: 'right' },
    { key: 'prev', label: 'Prev. certified', align: 'right' },
    { key: 'pct', label: 'Period %', align: 'right' },
    { key: 'applied', label: 'This period', align: 'right' },
    { key: 'cumulative', label: 'Cumulative', align: 'right' },
  ];
  const basisOf = (ln: Record<string, unknown>) => {
    const meta = parseJsonObject(ln.meta_json ?? ln.metaJson);
    const parts: string[] = [];
    if (meta.siteCumulativePct != null && meta.siteCumulativePct !== '') {
      parts.push(`Site ${pct(meta.siteCumulativePct)}`);
    }
    if (meta.evidenceRef) parts.push(str(meta.evidenceRef));
    return { text: parts.join(' · '), note: str(meta.evidenceNote) };
  };
  const hasBasis = sorted.some((ln) => {
    const b = basisOf(ln);
    return Boolean(b.text || b.note);
  });
  if (hasBasis) columns.push({ key: 'basis', label: 'Basis / evidence' });

  const printLines: SimplePrintLine[] = sorted.length
    ? sorted.map((ln, i) => {
        const code = str(ln.line_code || ln.lineCode);
        const description = str(ln.description, code || `Line ${i + 1}`);
        const applied = Number(ln.applied_amount ?? ln.appliedAmount ?? 0) || 0;
        const prev =
          Number(ln.previous_certified_amount ?? ln.previousCertifiedAmount ?? 0) || 0;
        const meta = parseJsonObject(ln.meta_json ?? ln.metaJson);
        const lineAmount =
          Number(meta.lineAmount ?? ln.unit_rate ?? ln.unitRate ?? applied) || 0;
        const periodPct =
          meta.thisPeriodPct != null
            ? Number(meta.thisPeriodPct)
            : lineAmount > 0
              ? Math.round((applied / lineAmount) * 10000) / 100
              : 0;
        const cumulative =
          meta.cumulativeAmount != null
            ? Number(meta.cumulativeAmount)
            : Math.round((prev + applied) * 100) / 100;
        const pctHint =
          periodPct > 0 ? `This period ${periodPct}% of entitlement line` : '';
        const basis = basisOf(ln);
        const extraLines = [pctHint, basis.note ? `Evidence: ${basis.note}` : ''].filter(Boolean);
        return {
          lineNumber: Number(ln.line_number ?? ln.lineNumber ?? i + 1),
          description,
          extraLines: extraLines.length ? extraLines : undefined,
          cells: {
            sno: String(i + 1),
            code,
            entitlement: money(lineAmount),
            prev: money(prev),
            pct: periodPct > 0 ? pct(periodPct) : '',
            applied: money(applied),
            cumulative: money(cumulative),
            ...(hasBasis ? { basis: basis.text } : {}),
          },
        };
      })
    : [
        {
          lineNumber: 1,
          description: 'Contract works (header valuation)',
          cells: {
            sno: '1',
            code: 'CONTRACT',
            entitlement: money(c.contractValue ?? c.contract_value),
            prev: money(terms.previousGrossCertified),
            pct: '',
            applied: money(pa.gross_applied_amount ?? pa.grossAppliedAmount),
            cumulative: money(
              pa.cumulative_gross_amount ??
                pa.cumulativeGrossAmount ??
                pa.gross_applied_amount
            ),
          },
        },
      ];

  const workGross = sorted.length
    ? sorted.reduce((s, ln) => s + (Number(ln.applied_amount ?? ln.appliedAmount) || 0), 0)
    : Number(pa.gross_applied_amount ?? pa.grossAppliedAmount ?? 0) || 0;

  const leftRows: Array<{ label: string; value: string }> = [
    { label: 'M/S', value: customerName || '—' },
  ];
  if (contractNo) {
    leftRows.push({
      label: 'Contract',
      value: contractTitle ? `${contractNo} — ${contractTitle}` : contractNo,
    });
  }
  if (projectLabel) leftRows.push({ label: 'Project', value: projectLabel });
  if (periodLabel) leftRows.push({ label: 'Period', value: periodLabel });

  const rightRows: Array<{ label: string; value: string }> = [
    { label: 'Application No.', value: appNo },
    { label: 'Document', value: 'Interim Payment Application' },
    { label: 'Status', value: status },
    { label: 'Currency', value: currency },
  ];
  if (pa.revision != null) rightRows.push({ label: 'Revision', value: str(pa.revision) });
  const issued = formatPrintDate(str(pa.submitted_at || pa.created_at) || null);
  if (issued) rightRows.push({ label: 'Date', value: issued });

  const contractValue = Number(c.contractValue ?? c.contract_value ?? c.contractSum ?? 0) || 0;
  const previousGross =
    Number(terms.previousGrossCertified ?? pa.previous_gross_certified) ||
    sorted.reduce(
      (s, ln) => s + (Number(ln.previous_certified_amount ?? ln.previousCertifiedAmount) || 0),
      0
    );
  const cumulativeGross =
    Number(pa.cumulative_gross_amount ?? pa.cumulativeGrossAmount) ||
    Math.round((previousGross + workGross) * 100) / 100;

  const termRows: Array<{ label: string; value: string }> = [];
  if (contractValue > 0) {
    termRows.push({
      label: 'Contract / approved value',
      value: `${currency} ${money(contractValue)}`,
    });
  }
  if (previousGross > 0 || sorted.length) {
    termRows.push({
      label: 'Previously certified (gross)',
      value: `${currency} ${money(previousGross)}`,
    });
  }
  if (terms.advanceRecoveryRatePct != null) {
    termRows.push({ label: 'Advance recovery rate', value: pct(terms.advanceRecoveryRatePct) });
  }
  if (terms.retentionRatePct != null) {
    termRows.push({ label: 'Retention rate', value: pct(terms.retentionRatePct) });
  }
  if (terms.remainingAdvanceBalance != null) {
    termRows.push({
      label: 'Remaining advance (cap)',
      value: `${currency} ${money(terms.remainingAdvanceBalance)}`,
    });
  }
  if (terms.retentionCap != null) {
    termRows.push({
      label: 'Retention remaining room',
      value: `${currency} ${money(terms.retentionCap)}`,
    });
  }

  const lineCountNote = sorted.length
    ? `This application values ${sorted.length} entitlement line${sorted.length === 1 ? '' : 's'} for the stated period against the Contract.`
    : 'This application is valued on a header commercial total (no entitlement lines recorded).';

  const ratesBlock =
    termRows.length > 0 ? (
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ccc' }}>
        <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 4 }}>
          Commercial rates &amp; reference
        </div>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <tbody>
            {termRows.map((row) => (
              <tr key={row.label}>
                <td style={{ padding: '1px 0', width: '55%' }}>{row.label}</td>
                <td style={{ padding: '1px 0', textAlign: 'right' }}>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null;

  const signaturesBlock = (
    <div style={{ marginTop: 12, fontSize: 11 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', paddingRight: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Submitted by (Contractor)</div>
              <div style={{ marginBottom: 18 }}>Name: _______________________________</div>
              <div style={{ marginBottom: 18 }}>Signature: __________________________</div>
              <div>Date: ________________________________</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', paddingLeft: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Received / acknowledged (Client)</div>
              <div style={{ marginBottom: 18 }}>Name: _______________________________</div>
              <div style={{ marginBottom: 18 }}>Signature: __________________________</div>
              <div>Date: ________________________________</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  // Split valuation vs signatures so the page planner can pack both onto page 1 when
  // space remains (a single inflated block was forcing an empty page-1 tail).
  const totalsBlock = (
    <div className="print-ipc-totals">
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td>Work gross (this period)</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(workGross)}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Gross applied</strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>
                {currency} {money(pa.gross_applied_amount ?? pa.grossAppliedAmount ?? workGross)}
              </strong>
            </td>
          </tr>
          <tr>
            <td>Less: Advance recovery</td>
            <td style={{ textAlign: 'right' }}>
              ({currency} {money(pa.advance_recovery_amount ?? pa.advanceRecoveryAmount)})
            </td>
          </tr>
          <tr>
            <td>Less: Retention withheld</td>
            <td style={{ textAlign: 'right' }}>
              ({currency} {money(pa.retention_withheld_amount ?? pa.retentionWithheldAmount)})
            </td>
          </tr>
          <tr>
            <td>Add: Retention released</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(pa.retention_released_amount ?? pa.retentionReleasedAmount)}
            </td>
          </tr>
          <tr>
            <td>Cumulative work gross (to date)</td>
            <td style={{ textAlign: 'right' }}>
              {currency} {money(cumulativeGross)}
            </td>
          </tr>
          <tr>
            <td style={{ paddingTop: 6 }}>
              <strong>Net amount applied this period</strong>
            </td>
            <td style={{ textAlign: 'right', paddingTop: 6 }}>
              <strong>
                {currency} {money(pa.net_applied_amount ?? pa.netAppliedAmount)}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      {ratesBlock}
      <p style={{ fontSize: 10, marginTop: 8, color: '#444' }}>
        Commercial valuation for client submission. Amounts are exclusive of VAT. Certification and
        Progress Tax Invoice follow separately under Project Billing.
      </p>
    </div>
  );

  const valuationRows = 7;
  const ratesRows = termRows.length > 0 ? 1 + termRows.length : 0;
  const totalsHeightPx =
    20 + valuationRows * LINE_HEIGHT_PX + ratesRows * LINE_HEIGHT_PX + (ratesRows ? 20 : 0) + 36;
  const signaturesHeightPx = 100;

  return buildSimplePrintDocument({
    docType: 'epc_payment_application',
    title: 'INTERIM PAYMENT APPLICATION',
    subtitle: contractTitle || contractNo || undefined,
    documentNumber: appNo,
    partyName: customerName || contractNo || appNo,
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    watermark: isOfficial ? undefined : { text: 'DRAFT' },
    continuationLabel: `${appNo} | ${customerName || contractNo || 'IPC'}`,
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [
      `Please find enclosed Interim Payment Application ${appNo} setting out the valuation of works executed for the stated period under the Contract. ${lineCountNote}`,
      'Net applied excludes VAT, which will be stated on the Progress Tax Invoice after certification.',
    ],
    columns,
    lines: printLines,
    tableTitle: 'Schedule of works claimed this period',
    finals: [
      // Do not keepTogether — packing totals alone onto page 1 avoids a blank first-page tail
      // when signatures need the next sheet.
      { id: 'totals', heightPx: totalsHeightPx },
      { id: 'signatures', heightPx: signaturesHeightPx },
    ],
    renderPayload: {
      finalBlocks: {
        totals: totalsBlock,
        signatures: signaturesBlock,
      },
    },
  });
}
